import React from 'react';

import { Button } from '@design-system';

import { cn } from '@/lib/utils';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export function TablePaginationControls({
  totalRows,
  firstRow,
  lastRow,
  pageSize,
  canPreviousPage,
  canNextPage,
  onPreviousPage,
  onNextPage,
  onPageSizeChange,
  responsive = false,
  alwaysShow = false,
}: {
  totalRows: number;
  firstRow: number;
  lastRow: number;
  pageSize: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onPageSizeChange: (pageSize: number) => void;
  responsive?: boolean;
  alwaysShow?: boolean;
}) {
  if (!alwaysShow && totalRows <= pageSize) return null;

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 py-5',
        responsive && 'flex-col items-stretch sm:flex-row sm:items-center',
      )}
    >
      <div
        className={cn(
          'flex items-center gap-4',
          responsive && 'flex-col-reverse items-stretch sm:flex-row sm:items-center',
        )}
      >
        <div className={cn('flex items-center gap-1', responsive && 'grid grid-cols-2 sm:flex')}>
          <Button
            variant="outline"
            size="default"
            onClick={onPreviousPage}
            disabled={!canPreviousPage}
            className="border-primary-300 text-primary-dark hover:bg-primary-100"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="default"
            onClick={onNextPage}
            disabled={!canNextPage}
            className="border-primary-300 text-primary-dark hover:bg-primary-100"
          >
            Next
          </Button>
        </div>
        <div className={responsive ? 'text-center sm:text-left' : 'hidden sm:block'}>
          <p
            className="text-sm text-gray-700"
            data-testid="table-pagination-summary"
          >
            Showing <span className="font-semibold">{firstRow}</span> to{' '}
            <span className="font-semibold">{lastRow}</span> of{' '}
            <span className="font-semibold">{totalRows}</span> results
          </p>
        </div>
      </div>

      <div
        className={cn('flex items-center gap-1', responsive && 'justify-center sm:justify-start')}
      >
        {PAGE_SIZE_OPTIONS.map(size => (
          <Button
            key={size}
            variant="outline"
            size="icon"
            onClick={() => onPageSizeChange(size)}
            className={cn(
              'h-9 w-9',
              size === pageSize
                ? 'bg-primary hover:bg-primary-hover border-primary text-white'
                : 'border-primary-300 text-primary-dark hover:bg-primary-100',
            )}
          >
            {size}
          </Button>
        ))}
        <span className="ml-1 text-sm text-gray-700">per page</span>
      </div>
    </div>
  );
}
